// Central content for SML Municipal Consulting
import {
  FileSearch, ClipboardCheck, Gauge, FileCheck2, RefreshCcw,
  Building2, Home, Store, Warehouse, Users, Landmark, ShieldCheck,
  BadgeCheck, LineChart, MapPin, Ruler, ScanLine, Cpu, SearchCheck,
  Activity, Split, PhoneCall, ClipboardList, Cog, Repeat2,
} from "lucide-react";

export const CONTACT = {
  phone: "081 560 7949",
  phoneRaw: "+27815607949",
  whatsapp: "27815607949",
  email: "info@smlmunicipalconsulting.co.za",
  addressLines: ["134 River Road", "Barbeque Downs", "Kyalami", "South Africa"],
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Account Management", href: "#account-management" },
  { label: "Metering Services", href: "#metering" },
  { label: "Why Choose Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export const STATS = [
  { value: "8+", unit: "Years", label: "Industry Experience" },
  { value: "2,600+", unit: "", label: "Municipal Accounts Reviewed & Resolved" },
  { value: "7+", unit: "", label: "Major Municipalities Covered" },
  { value: "End-to-End", unit: "", label: "Municipal Query Resolution" },
];

export const SERVICES = [
  {
    id: "billing",
    icon: FileSearch,
    title: "Municipal Billing Disputes & Queries",
    blurb: "We investigate incorrect bills, estimated readings, tariff errors and unexplained balances on your behalf.",
    points: [
      "Incorrect municipal bills", "Unexpected charges", "Incorrect & estimated meter readings",
      "Abnormally high consumption", "Incorrect tariffs & duplicate charges", "Unallocated payments",
      "Account adjustments & unexplained balances", "Electricity, water, sewer & refuse queries", "Property rates queries",
    ],
    cta: "Resolve My Billing Query",
  },
  {
    id: "management",
    icon: ClipboardCheck,
    title: "Municipal Account Management",
    blurb: "Ongoing monthly monitoring of your municipal accounts to catch irregularities early.",
    points: [
      "Monthly statement reviews", "Unexpected charge identification", "Tariff monitoring",
      "Meter reading monitoring", "Estimated billing identification", "Consumption trend monitoring",
      "Payment allocation checks", "Adjustment reviews", "Municipal query facilitation", "Account correction follow-up",
    ],
    cta: "View Account Management Packages",
    ctaHref: "#pricing",
  },
  {
    id: "metering-svc",
    icon: Gauge,
    title: "Metering Services",
    blurb: "Electricity and water metering solutions, from installation to verification and monitoring.",
    points: [
      "Electricity & water meter installation", "Sub-metering", "Meter reading services",
      "Smart meter programming", "Meter audits & verification", "Consumption monitoring", "Bulk-to-individual sub-metering",
    ],
    cta: "Discuss a Metering Solution",
    ctaHref: "#metering",
  },
  {
    id: "clearance",
    icon: FileCheck2,
    title: "Municipal Clearance Applications",
    blurb: "We help property sellers manage the municipal clearance process from application to resolution.",
    points: [
      "Applying for clearance figures", "Following up outstanding applications", "Investigating account problems",
      "Resolving billing issues", "Checking balances", "Communicating with the municipality", "Monitoring applications",
    ],
    cta: "Get Clearance Assistance",
  },
  {
    id: "new-account",
    icon: RefreshCcw,
    title: "New Account, Refund & Account Closure",
    blurb: "Assistance for buyers and sellers with opening, closing, updating and refund applications.",
    points: [
      "Opening new municipal accounts", "Closing existing accounts", "Updating ownership information",
      "Confirming registration dates", "Refund applications & follow-ups", "Account balance investigations",
      "Billing issues linked to ownership changes",
    ],
    cta: "Get Account Assistance",
  },
];

export const PRICING = [
  {
    name: "Rates & Refuse",
    tier: "Service 1",
    price: "R650",
    period: "/ month",
    highlighted: false,
    intro: "Suitable for monitoring:",
    features: [
      "Property rates", "Refuse charges", "Unexpected charges", "Duplicate charges",
      "Incorrect tariffs", "Unallocated payments", "Account adjustments",
      "Rates-related discrepancies", "Refuse-related discrepancies",
    ],
    cta: "Choose This Package",
  },
  {
    name: "Complete Municipal Account Management",
    tier: "Service 3",
    price: "R1,650",
    period: "/ month",
    highlighted: true,
    badge: "Most Comprehensive",
    intro: "Rates + Water + Sewer + Electricity + Refuse, plus:",
    features: [
      "Tariff monitoring", "Account adjustment monitoring", "Payment allocation monitoring",
      "Meter number verification", "Physical vs billed reading comparisons", "Estimated consumption investigations",
      "Monthly meter monitoring", "Faulty meter queries", "Meter replacement assistance", "Abnormal consumption identification",
    ],
    cta: "Choose Complete Management",
  },
  {
    name: "Water, Sewer & Electricity",
    tier: "Service 2",
    price: "R1,150",
    period: "/ month",
    highlighted: false,
    intro: "Includes monitoring of:",
    features: [
      "Water, sewer & electricity", "Abnormal consumption", "Meter readings & estimated billing",
      "Duplicate charges & incorrect tariffs", "Unallocated payments", "Account adjustments",
      "Meter numbers", "Physical vs billed readings", "Faulty meter & replacement queries",
    ],
    cta: "Choose This Package",
  },
];

export const METERING = [
  { icon: Ruler, title: "Meter Installation & Sub-Metering", text: "Install meters and sub-meters for individual units, tenants, shops, buildings and sections." },
  { icon: ScanLine, title: "Meter Reading Services", text: "Accurate electricity and water consumption readings for billing and account verification." },
  { icon: Cpu, title: "Smart Meter Programming", text: "Setup and programming of compatible smart metering systems." },
  { icon: SearchCheck, title: "Meter Audits & Verification", text: "Investigate incorrect allocations, meter discrepancies and potential metering errors." },
  { icon: Activity, title: "Consumption Monitoring", text: "Monitor usage patterns and identify possible consumption abnormalities." },
  { icon: Split, title: "Bulk-to-Individual Sub-Metering", text: "Measure consumption at individual tenant, shop or unit level where a property is supplied through a bulk municipal meter." },
];

export const ASSIST = [
  { icon: Building2, label: "Residential Complexes" },
  { icon: ShieldCheck, label: "Security Estates" },
  { icon: Building2, label: "Apartment Buildings" },
  { icon: Store, label: "Shopping Centres" },
  { icon: Store, label: "Retail Properties" },
  { icon: Building2, label: "Office Parks" },
  { icon: Building2, label: "Commercial Buildings" },
  { icon: Warehouse, label: "Industrial Properties" },
  { icon: Users, label: "Property Management Companies" },
  { icon: Landmark, label: "Body Corporates" },
  { icon: Users, label: "HOAs" },
  { icon: Home, label: "Landlords" },
  { icon: Home, label: "Property Owners" },
  { icon: Store, label: "Businesses" },
];

export const MUNICIPALITIES = [
  "City of Johannesburg", "City of Tshwane", "City of Cape Town", "City of Ekurhuleni",
  "Mangaung Metropolitan Municipality", "Mogale City Local Municipality", "Eskom", "Other municipalities on request",
];

export const WHY = [
  { icon: BadgeCheck, title: "Dedicated Municipal Account Specialists", text: "Professional support for municipal account queries and account management." },
  { icon: ClipboardList, title: "Complete Case Tracking & Follow-Up", text: "We manage cases from submission through follow-up and resolution." },
  { icon: Building2, title: "Residential, Commercial & Industrial Expertise", text: "Support for clients across multiple property categories." },
  { icon: FileSearch, title: "Municipal Billing & Account Investigation", text: "Identify and investigate potential municipal billing discrepancies." },
  { icon: Gauge, title: "Metering & Utility Management Support", text: "Electricity and water metering, monitoring and account support." },
  { icon: MapPin, title: "Nationwide Municipal Consulting Support", text: "We assist clients with municipal matters across South Africa." },
];

export const STEPS = [
  { icon: PhoneCall, title: "Tell Us About Your Issue", text: "Complete the online quotation / enquiry form." },
  { icon: SearchCheck, title: "We Assess Your Requirements", text: "The SML team reviews the information provided." },
  { icon: Cog, title: "We Manage the Process", text: "SML assists with the required municipal query, application, investigation or service." },
  { icon: Repeat2, title: "We Track & Follow Up", text: "The matter is monitored and followed up through the relevant municipal process." },
];

export const FORM_OPTIONS = {
  service: [
    "Billing Disputes & Queries", "Metering Services", "Municipal Account Management",
    "New Account, Refund & Account Closure", "Municipal Clearance Application",
  ],
  propertyType: [
    "Full Title Residential Property", "Sectional Title Residential Property",
    "Full Title Business / Industrial Property", "Sectional Title Business / Industrial Property",
    "Vacant Land", "Agricultural Property", "Other",
  ],
  municipality: [
    "City of Johannesburg", "City of Tshwane", "City of Cape Town", "City of Ekurhuleni",
    "Mbombela Municipality", "Mangaung Metropolitan Municipality", "Mogale City Local Municipality", "Eskom", "Other",
  ],
};

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1636706519609-988babca3dd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  meters: "https://images.unsplash.com/photo-1555009784-ae7e7d1b97aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  metersAlt: "https://images.unsplash.com/photo-1684684383508-261dd0e8f467?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  residential: "https://images.unsplash.com/photo-1650202034003-39d9b74045fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  commercial: "https://images.unsplash.com/photo-1774544368113-b66148dab467?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  meeting: "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
};
